import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Router } from "@angular/router";
import {
  CalculatePremiumRequest,
  CalculatePremiumResponse,
  GeneralConfirmationModalData,
  GeneralConfirmationModalResult,
  PaymentFrequency,
  PetInsuranceData
} from "../../../models";
import { DataStorageService } from "../../../services/data-storage.service";
import { ApiHttpService } from "../../../services/api-http.service";
import { MatDialog } from "@angular/material/dialog";
import { DialogService } from "../../../services/dialog.service";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";
import { TranslateService } from "@ngx-translate/core";
import { map, Observable } from "rxjs";
import { hashFields } from "../../../utils";

@Component({
  selector: 'cpf-edit-insurance-info',
  templateUrl: './edit-insurance-info.component.html',
  styleUrls: ['./edit-insurance-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EditInsuranceInfoComponent implements OnInit {
  @Input()
  petData!: PetInsuranceData;

  @Input()
  showBackButton: boolean = true;

  @Input()
  isDesktop: boolean = false;

  @Output()
  closeModal: EventEmitter<void> = new EventEmitter<void>();

  showPackage: string = 'M';
  selectedPackage: string = 'M';
  preExistingConditionsCoverage: boolean = false;
  medicalFoodCoverage: boolean = false;
  preventiveCoverage: boolean = false;
  private readonly catFranchiseOptions = [
    { value: '0', label: 'CHF 0' },
    { value: '150', label: 'CHF 150' },
    { value: '300', label: 'CHF 300' },
    { value: '500', label: 'CHF 500' },
    { value: '1000', label: 'CHF 1000' },
  ];
  private readonly dogFranchiseOptions = [
    { value: '0', label: 'CHF 0' },
    { value: '300', label: 'CHF 300' },
    { value: '500', label: 'CHF 500' },
    { value: '1000', label: 'CHF 1000' },
    { value: '2000', label: 'CHF 2000' },
  ];
  franchiseOptions = this.catFranchiseOptions;
  // the accident package offers no add-on coverages, only these deductibles and only the 80% cost coverage
  private readonly accidentFranchiseValues = ['300', '500', '1000'];

  costOptions = [
    { value: '0', label: '100%' },
    { value: '20', label: '80%' },
  ];
  selectedFranchise = this.franchiseOptions[2];
  selectedCost = this.costOptions[1];
  selectedPaymentInterval = PaymentFrequency.MONTHLY;
  fullPrice: number = 0;
  private premiumCache = new Map<PaymentFrequency, CalculatePremiumResponse>();
  calculatedPremiumResponse: CalculatePremiumResponse = {
    basePremium: 0,
    totalPremium: 0,
    liabilityPremium: 0,
    globalCoveragePremium: 0,
    preexistingConditionsPremium: 0,
    medicalFoodCoveragePremium: 0,
    preventionCoveragePremium: 0,
  };

  linkOne = "https://calingopublicdownloads.s3.eu-central-1.amazonaws.com/pet/latest/en/gic.pdf";
  linkTwo = "https://calingopublicdownloads.s3.eu-central-1.amazonaws.com/pet/latest/en/product_information.pdf";

  private initStateHash = '';

  public InfoMessages = InfoMessages;
  private initPetData: PetInsuranceData | undefined;

  constructor(private router: Router,
              private dataStorageService: DataStorageService,
              private elementRef: ElementRef,
              private apiHttpService: ApiHttpService,
              private dialog: MatDialog,
              private dialogService: DialogService,
              private translateService: TranslateService) {
  }

  ngOnInit() {
    // Deep copy of petData to avoid changing the original data
    this.initPetData = JSON.parse(JSON.stringify(this.petData));
    this.showPackage = this.petData.product_bundle;
    this.selectedPackage = this.petData.product_bundle;
    this.preExistingConditionsCoverage = this.petData.preexisting_conditions_coverage.preexisting_conditions;
    this.medicalFoodCoverage = this.petData.medical_food_coverage.coverage_selected;
    this.preventiveCoverage = this.petData.preventive_care_coverage.coverage_selected;
    this.franchiseOptions = this.petData.pet_species === 'dog' ? this.dogFranchiseOptions : this.catFranchiseOptions;
    this.selectedFranchise = this.franchiseOptions.find(option => option.value === this.petData.annual_franchise.toString())
      || this.franchiseOptions.find(option => option.value === '300')!;
    this.selectedCost = this.costOptions.find(option => option.value === (this.petData.deductible_percentage_until_9_years * 100).toString()) || this.costOptions[1];
    this.selectedPaymentInterval = this.petData.payment_interval;

    this.calculatePremium();

    this.initStateHash = this.hashEditInsuranceFields();

    setTimeout(() => {
      this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  private buildPetList() {
    const currentPetEntry = {
      petName: this.petData.pet_name,
      breed: this.petData.breed,
      breedType: this.petData.breed_type ? 'cross' : (this.petData.breed.includes('mischling') ? 'mixed' : 'pedigree'),
      petGender: this.petData.pet_gender[0],
      species: this.petData.pet_species,
      petBirthdate: new Date(this.petData.birthdate).toISOString().split('T')[0],
      copayment: Number.parseInt(this.selectedCost.value),
      excess: Number.parseInt(this.selectedFranchise.value),
      coverageLevel: this.showPackage.toLowerCase(),
      neutered: this.petData.neutered,
      globalCoverage: true,
      preexistingConditions: this.preExistingConditionsCoverage,
      medicalFoodCoverage: this.medicalFoodCoverage,
      preventionCoverage: this.preventiveCoverage,
      housecat: this.petData.housecat ?? null,
      tallDog: this.petData.pet_species === 'dog' && this.petData.breed.includes('mischling_gross'),
      liability: false,
    };

    const otherPetEntries = this.dataStorageService.getAllData
      .filter(p => p.full_contract_number !== this.petData.full_contract_number && p.contractStatus !== 'expired')
      .map(p => CalculatePremiumRequest.petEntryFromInsuranceData(p));

    return [currentPetEntry, ...otherPetEntries];
  }

  private applyPremiumFromCache() {
    const response = this.premiumCache.get(this.selectedPaymentInterval);
    if (!response) return;
    // When multiple pets are sent, root-level totals are the sum of all pets.
    // petPremiums[0] is always the current pet's individual breakdown (with multi-pet discount applied).
    const currentPetPremium = response.petPremiums?.[0] ?? response;
    this.calculatedPremiumResponse = {
      basePremium: this.roundPrices(currentPetPremium.basePremium),
      totalPremium: this.roundPrices(currentPetPremium.totalPremium),
      liabilityPremium: this.roundPrices(currentPetPremium.liabilityPremium),
      globalCoveragePremium: this.roundPrices(currentPetPremium.globalCoveragePremium),
      preexistingConditionsPremium: this.roundPrices(currentPetPremium.preexistingConditionsPremium),
      // medicalFood and prevention premiums are not in petPremiums breakdown — use root-level
      medicalFoodCoveragePremium: this.roundPrices(response.medicalFoodCoveragePremium),
      preventionCoveragePremium: this.roundPrices(response.preventionCoveragePremium),
    };
  }

  async calculatePremium() {
    const petList = this.buildPetList();
    const frequencies = Object.values(PaymentFrequency);

    const responses = await Promise.all(
      frequencies.map(freq =>
        this.apiHttpService.calculatePremium(new CalculatePremiumRequest({ petList, paymentFrequency: freq }))
      )
    );

    this.premiumCache.clear();
    frequencies.forEach((freq, i) => this.premiumCache.set(freq, responses[i]));
    this.applyPremiumFromCache();

    const monthlyResponse = this.premiumCache.get(PaymentFrequency.MONTHLY)!;
    const monthlyPetPremium = monthlyResponse.petPremiums?.[0] ?? monthlyResponse;
    this.fullPrice = this.roundPrices(monthlyPetPremium.totalPremium * this.getMultiplier());
  }

  private getMultiplier(): number {
    const activePetCount = this.dataStorageService.getAllData
      .filter(p => p.contractStatus !== 'expired').length;
    if (activePetCount >= 3) return 0.9;
    if (activePetCount >= 2) return 0.95;
    return 1;
  }

  onPaymentIntervalChange() {
    this.applyPremiumFromCache();
  }

  hashEditInsuranceFields(): string {
    const dataToHash = {
      selectedPackage: this.selectedPackage,
      preexisting_conditions_coverage: this.preExistingConditionsCoverage,
      medical_food_coverage: this.medicalFoodCoverage,
      preventive_coverage: this.preventiveCoverage,
      annual_franchise: Number.parseInt(this.selectedFranchise.value),
      deductible_percentage_until_9_years: Number.parseInt(this.selectedCost.value),
      payment_interval: this.selectedPaymentInterval,
    };

    return hashFields(dataToHash);
  }

  isEditInsuranceFieldsChanged(): boolean {
    return this.hashEditInsuranceFields() !== this.initStateHash;
  }

  roundPrices(price: number) {
    // 8.04 -> 8.05 | 8.04 -> 160.8 -> 161 -> 8.05
    return Math.round(price * 20) / 20;
  }

  setShowPackage(packageType: string) {
    this.showPackage = packageType;
    this.applyAccidentRestrictions();
    this.calculatePremium();
  }

  isAccidentPackage(): boolean {
    return this.showPackage === 'A';
  }

  getFranchiseOptions() {
    if (!this.isAccidentPackage()) {
      return this.franchiseOptions;
    }
    return this.franchiseOptions.filter(option => this.accidentFranchiseValues.includes(option.value));
  }

  getCostOptions() {
    return this.isAccidentPackage() ? this.costOptions.filter(option => option.value === '20') : this.costOptions;
  }

  // removes the selections that the accident package does not offer
  private applyAccidentRestrictions(): void {
    if (!this.isAccidentPackage()) {
      return;
    }
    this.preExistingConditionsCoverage = false;
    this.medicalFoodCoverage = false;
    this.preventiveCoverage = false;

    const franchiseOptions = this.getFranchiseOptions();
    const franchise = franchiseOptions.find(option => option.value === this.selectedFranchise.value);
    if (!franchise) {
      const defaultValue = this.petData.pet_species === 'dog' ? '500' : '300';
      this.selectedFranchise = franchiseOptions.find(option => option.value === defaultValue) || franchiseOptions[0];
    } else {
      // the template compares the options by object, so always use the option of the list
      this.selectedFranchise = franchise;
    }

    const costOptions = this.getCostOptions();
    this.selectedCost = costOptions.find(option => option.value === this.selectedCost.value) || costOptions[0];
  }

  selectPackage(packageType: string) {
    this.selectedPackage = packageType;
  }

  toggleAdditionalCoverage() {
    if (!this.preExistingConditionsCoverage) {
      const message = InfoMessages.AddtlCoveragePreexistingConditions;
      const title = 'edit-insurance-info.info-modal.title-' + message;
      const messageText = 'edit-insurance-info.info-modal.message-' + message;
      this.translateService.get([title, messageText]).subscribe((translations) => {
        const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
          data: {
            title: translations[title],
            message: `<div class="text-left">${translations[messageText]}</div>`,
            useInnerHtml: true,
            confirmButton: GeneralConfirmationModalResult.Confirm,
            cancelButton: GeneralConfirmationModalResult.Cancel,
            confirmButtonText: "OK"
          } as GeneralConfirmationModalData
        });

        dialogRef.afterClosed().subscribe((result: GeneralConfirmationModalResult) => {
          if (result === GeneralConfirmationModalResult.Confirm) {
            this.preExistingConditionsCoverage = !this.preExistingConditionsCoverage;
            this.calculatePremium();
          }
        });
      });
    } else {
      this.preExistingConditionsCoverage = !this.preExistingConditionsCoverage;
      this.calculatePremium();
    }
  }

  toggleMedicalFoodCoverage() {
    this.medicalFoodCoverage = !this.medicalFoodCoverage;
    this.calculatePremium();
  }

  togglePreventiveCoverage() {
    this.preventiveCoverage = !this.preventiveCoverage;
    this.calculatePremium();
  }

  navigateBack() {
    this.router.navigate(['calingo', 'pet', 'insurance', 'details']);
  }

  async saveChanges() {
    if (!this.initPetData) {
      return
    }

    const translations = this.translateService.instant(['edit-insurance-info.modals.save-changes.title', 'edit-insurance-info.modals.save-changes.message']);
    const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: translations['edit-insurance-info.modals.save-changes.title'],
        message: translations['edit-insurance-info.modals.save-changes.message'],
        confirmButton: GeneralConfirmationModalResult.Confirm,
        cancelButton: GeneralConfirmationModalResult.Cancel,
      } as GeneralConfirmationModalData
    });

    dialogRef.afterClosed().subscribe(async (result: GeneralConfirmationModalResult) => {
      if (result === GeneralConfirmationModalResult.Confirm) {
        if (!this.initPetData) {
          return
        }

        try {
          const sent = await this.apiHttpService.updatePolicy({
            full_contract_number: this.petData.full_contract_number,
            customer_id: this.petData.customer_id,
            update_pet_info: {
              old_value: {
                breed: this.initPetData.breed,
                breed_type: this.initPetData.breed_type,
                birthdate: this.initPetData.birthdate,
                neutered: this.initPetData.neutered,
                pet_gender: this.initPetData.pet_gender,
                pet_name: this.initPetData.pet_name,
                pet_species: this.initPetData.pet_species,
              },
              new_value: {
                breed: this.initPetData.breed,
                breed_type: this.initPetData.breed_type,
                birthdate: this.initPetData.birthdate,
                neutered: this.initPetData.neutered,
                pet_gender: this.initPetData.pet_gender,
                pet_name: this.initPetData.pet_name,
                pet_species: this.initPetData.pet_species,
              }
            },
            update_insurance_info: {
              old_value: {
                product_bundle: this.initPetData.product_bundle,
                preexisting_conditions_coverage: this.initPetData.preexisting_conditions_coverage.preexisting_conditions,
                medical_food_coverage: this.initPetData.medical_food_coverage.coverage_selected,
                preventive_care_coverage: this.initPetData.preventive_care_coverage.coverage_selected,
                annual_franchise: this.initPetData.annual_franchise,
                deductible_percentage_until_9_years: this.initPetData.deductible_percentage_until_9_years,
                payment_interval: this.initPetData.payment_interval,
              },
              new_value: {
                product_bundle: this.showPackage,
                preexisting_conditions_coverage: this.preExistingConditionsCoverage,
                medical_food_coverage: this.medicalFoodCoverage,
                preventive_care_coverage: this.preventiveCoverage,
                annual_franchise: Number.parseInt(this.selectedFranchise.value),
                deductible_percentage_until_9_years: Number.parseInt(this.selectedCost.value),
                payment_interval: this.selectedPaymentInterval,
              }
            }
          })

          // a failed send keeps the customer on the form, so they can try again
          if (!sent) {
            this.showSaveError();
            return;
          }

          this.initStateHash = this.hashEditInsuranceFields();

          const successTranslations = this.translateService.instant(['edit-insurance-info.modals.save-success.title', 'edit-insurance-info.modals.save-success.message']);
          this.dialogService.openDialog(GeneralConfirmationModalComponent, {
            data: {
              title: successTranslations['edit-insurance-info.modals.save-success.title'],
              message: successTranslations['edit-insurance-info.modals.save-success.message'],
              confirmButton: GeneralConfirmationModalResult.Confirm,
              confirmButtonText: 'OK'
            } as GeneralConfirmationModalData
          }).afterClosed().subscribe(() => this.leave());
        } catch (e) {
          console.error('Error updating policy', e);
          this.showSaveError();
        }
      }
    });
  }

  // the side modal on desktop listens to closeModal, the mobile page does not
  private leave() {
    if (this.closeModal.observed) {
      this.closeModal.emit();
      return;
    }

    this.navigateBack();
  }

  private showSaveError() {
    const translations = this.translateService.instant(['edit-insurance-info.modals.save-error.title', 'edit-insurance-info.modals.save-error.message']);
    this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: translations['edit-insurance-info.modals.save-error.title'],
        message: translations['edit-insurance-info.modals.save-error.message'],
        confirmButton: GeneralConfirmationModalResult.Confirm,
        confirmButtonText: 'OK'
      } as GeneralConfirmationModalData
    });
  }

  preExistingConditionsCoverageCostPerMonth() {
    return this.calculatedPremiumResponse.preexistingConditionsPremium ? this.roundPrices(this.calculatedPremiumResponse.preexistingConditionsPremium / 12) : '0.00';
  }

  medicalFoodCostPerMonth() {
    return this.calculatedPremiumResponse.medicalFoodCoveragePremium ? this.roundPrices(this.calculatedPremiumResponse.medicalFoodCoveragePremium / 12) : '0.00';
  }

  preventiveCostPerMonth() {
    return this.calculatedPremiumResponse.preventionCoveragePremium ? this.roundPrices(this.calculatedPremiumResponse.preventionCoveragePremium / 12) : '0.00';
  }

  baseCostPerSelectedSubscriptionInterval() {
    let divider = 12;
    switch (this.selectedPaymentInterval) {
      case PaymentFrequency.BIYEARLY:
        divider = 2;
        break;
      case PaymentFrequency.QUARTERLY:
        divider = 4;
        break;
      case PaymentFrequency.MONTHLY:
        divider = 12;
        break;
      case PaymentFrequency.YEARLY:
        divider = 1;
        break;
      default:
        divider = 12;
        break;
    }

    return this.calculatedPremiumResponse.basePremium ? this.roundPrices(this.calculatedPremiumResponse.basePremium / divider) : '0.00';
  }

  baseCostPerMonth() {
    return this.calculatedPremiumResponse.basePremium ? this.roundPrices(this.calculatedPremiumResponse.basePremium / 12) : '0.00';
  }

  costPerSelectedSubscriptionInterval() {
    let divider = 12;
    switch (this.selectedPaymentInterval) {
      case PaymentFrequency.BIYEARLY:
        divider = 2;
        break;
      case PaymentFrequency.QUARTERLY:
        divider = 4;
        break;
      case PaymentFrequency.MONTHLY:
        divider = 12;
        break;
      case PaymentFrequency.YEARLY:
        divider = 1;
        break;
      default:
        divider = 12;
        break;
    }

    return this.calculatedPremiumResponse.totalPremium ? this.roundPrices(this.calculatedPremiumResponse.totalPremium / divider) : '0.00';
  }

  costPerMonth() {
    return this.calculatedPremiumResponse.totalPremium ? this.roundPrices(this.calculatedPremiumResponse.totalPremium / 12) : '0.00';
  }

  savingsPerInterval(): number {
    const multiplier = this.getMultiplier();
    if (multiplier >= 1) return 0;
    let divisor = 12;
    switch (this.selectedPaymentInterval) {
      case PaymentFrequency.BIYEARLY: divisor = 2; break;
      case PaymentFrequency.QUARTERLY: divisor = 4; break;
      case PaymentFrequency.MONTHLY: divisor = 12; break;
      case PaymentFrequency.YEARLY: divisor = 1; break;
    }
    return this.roundPrices(this.fullPrice / multiplier / divisor - this.fullPrice / divisor);
  }

  getSubscriptionInterval() {
    const values = Object.values(PaymentFrequency);
    const index = values.indexOf(this.selectedPaymentInterval) || 0;
    const interval = Object.keys(PaymentFrequency)[index];
    const intervalLabel = this.translateService.instant(`common.subscription-interval.${interval}`);
    return intervalLabel;
  }

  showInfo(message: InfoMessages, useInnerHtml: boolean = false) {
    const title = 'edit-insurance-info.info-modal.title-' + message;
    const messageText = 'edit-insurance-info.info-modal.message-' + message;
    this.translateService.get([title, messageText]).subscribe((translations) => {
      this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations[title],
          message: `<div class="text-left">${translations[messageText]}</div>`,
          useInnerHtml: useInnerHtml,
          confirmButton: GeneralConfirmationModalResult.Confirm,
          confirmButtonText: "OK",
        } as GeneralConfirmationModalData
      });
    });
  }

  canDeactivate(): boolean | Observable<boolean> {
    const translations = this.translateService.instant(['edit-insurance-info.modals.leave-unsaved.title', 'edit-insurance-info.modals.leave-unsaved.message']);
    return !this.isEditInsuranceFieldsChanged() || this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: translations['edit-insurance-info.modals.leave-unsaved.title'],
        message: translations['edit-insurance-info.modals.leave-unsaved.message'],
        confirmButton: GeneralConfirmationModalResult.Confirm,
        cancelButton: GeneralConfirmationModalResult.Cancel,
      } as GeneralConfirmationModalData
    }).afterClosed().pipe(map((result: GeneralConfirmationModalResult) => {
      return result === GeneralConfirmationModalResult.Confirm;
    }));
  }

  protected readonly PaymentFrequency = PaymentFrequency;
}

enum InfoMessages {
  AddtlCoverageGlobalCoverage = "addtl-coverage-global-coverage",
  AddtlCoveragePreexistingConditions = "addtl-coverage-preexisting-conditions",
  AdvancedTreatment = "advanced-treatment",
  AlternativeTreatment = "alternative-treatment",
  AnnualVetFee = "annual-vet-fee",
  BehaviouralTreatment = "behavioural-treatment",
  Copayment = "copayment",
  DentalCover = "dental-cover",
  EmergencyCare = "emergency-care",
  Excess = "excess",
  MedicinalFood = "medicinal-food",
  PreventiveTreatment = "preventive-treatment",
  TreatmentAccidentsAndIllness = "treatment-accidents-and-illness",
  PaymentInterval = "payment-interval",
}
